import { getComboColorPair } from '../../charts/shared/palettes';
import { J, JN, TITLE_SPACING, fileHeader } from './shared';

export function genCombo(chartType, config, t, data) {
  const colorPair = getComboColorPair(config, t);
  const barColor = colorPair.primary;
  const lineColor = colorPair.secondary;
  const lineWidth = config.lineWidth || 2;
  const showGrid = !!config.showGrid;
  const showMarkers = !!config.showMarkers;
  const hasTitle = !!(config.title && String(config.title).trim());
  const topMargin = hasTitle ? 45 : 20;

  const barVals = data.map(d => d.barValue);
  const lineVals = data.map(d => d.lineValue);
  const maxBarVal = Math.max(...barVals, 0);
  const minLineVal = Math.min(...lineVals);
  const maxLineVal = Math.max(...lineVals);

  return (
    fileHeader(['@visx/scale', '@visx/group', '@visx/grid', '@visx/axis'], 'combo') +
    `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { GridRows } from '@visx/grid';
import { AxisLeft, AxisBottom, AxisRight } from '@visx/axis';

const DATA = ${JN(data)};
const BAR_COLOR = ${J(barColor)};
const LINE_COLOR = ${J(lineColor)};
const THEME = ${JN(t)};
const MAX_BAR_VAL = ${maxBarVal};
const MIN_LINE_VAL = ${minLineVal};
const MAX_LINE_VAL = ${maxLineVal};

export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${topMargin}, right: 70, bottom: 70, left: 60 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xScale = useMemo(() => scaleBand({
    domain: DATA.map(d => d.name),
    range: [0, innerWidth],
    padding: 0.35,
  }), [innerWidth]);

  const leftScale = useMemo(() => scaleLinear({
    domain: [0, MAX_BAR_VAL * 1.15],
    range: [innerHeight, 0],
    nice: true,
  }), [innerHeight]);

  const rightScale = useMemo(() => scaleLinear({
    domain: [MIN_LINE_VAL * 0.85, MAX_LINE_VAL * 1.1],
    range: [innerHeight, 0],
    nice: true,
  }), [innerHeight]);

  const bandwidth = xScale.bandwidth();
  const linePoints = DATA.map(d => ((xScale(d.name) || 0) + bandwidth / 2) + ',' + rightScale(d.lineValue)).join(' ');

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>
      ${hasTitle ? `<div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 60, fontSize: 18, fontWeight: 400, color: THEME.titleColor }}>${String(config.title).replace(/</g, '&lt;')}</div>` : ''}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          ${showGrid ? `<GridRows scale={leftScale} width={innerWidth} stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} strokeDasharray="3 3" />` : ''}
          {DATA.map((d, i) => {
            const x = xScale(d.name) || 0;
            const barH = innerHeight - leftScale(d.barValue);
            return (
              <rect key={'bar-' + i} x={x} y={innerHeight - Math.max(0, barH)} width={bandwidth} height={Math.max(0, barH)} fill={BAR_COLOR} opacity={0.85} />
            );
          })}
          <polyline points={linePoints} fill="none" stroke={LINE_COLOR} strokeWidth={${lineWidth}} strokeLinejoin="round" strokeLinecap="round" />
          ${showMarkers ? `{DATA.map((d, i) => (
            <circle key={'mk-' + i} cx={(xScale(d.name) || 0) + bandwidth / 2} cy={rightScale(d.lineValue)} r={4} fill={LINE_COLOR} />
          ))}` : ''}
          <AxisLeft scale={leftScale} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: BAR_COLOR, fontSize: 11, dx: -8, textAnchor: 'end' })} />
          <AxisRight scale={rightScale} left={innerWidth} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: LINE_COLOR, fontSize: 11, dx: 8, textAnchor: 'start' })} />
          <g>
            <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} />
            {DATA.map((d, i) => {
              const cx = (xScale(d.name) || 0) + bandwidth / 2;
              const cy = innerHeight + 10;
              return (
                <text key={'xt-' + i} x={cx} y={cy} textAnchor="end" fontSize={11} fill={THEME.tickColor} transform={'rotate(-30, ' + cx + ', ' + cy + ')'}>{d.name}</text>
              );
            })}
          </g>
          ${config.xlabel ? `<text x={innerWidth / 2} y={innerHeight + 60} textAnchor="middle" fontSize={12} fill={THEME.labelColor}>${config.xlabel}</text>` : ''}
          ${config.leftLabel ? `<text x={-innerHeight / 2} y={-42} transform="rotate(-90)" textAnchor="middle" fontSize={12} fill={BAR_COLOR}>${config.leftLabel}</text>` : ''}
          ${config.rightLabel ? `<text x={innerHeight / 2} y={-(innerWidth + 52)} transform="rotate(90)" textAnchor="middle" fontSize={12} fill={LINE_COLOR}>${config.rightLabel}</text>` : ''}
        </Group>
      </svg>
    </div>
  );
}
`
  );
}
